// Unit conversion system for built-in metrics
// Allows users to view metrics in their preferred units without affecting backend storage

export type UnitType = 
  // Volume
  | 'gal' | 'L' | 'cups'
  // Distance
  | 'mi' | 'km'
  // Time
  | 'min' | 'hrs'
  // Weight (for future use)
  | 'lbs' | 'kg'
  // Default units
  | 'g' | 'kcal' | 'rating' | 'boolean' | 'commits' | '%' | 'steps';

export interface UnitGroup {
  baseUnit: UnitType;
  alternateUnits: UnitType[];
  conversions: Partial<Record<UnitType, number>>; // Multiplier from base unit
}

// Define unit groups and their conversion factors
export const UNIT_GROUPS: Record<string, UnitGroup> = {
  volume: {
    baseUnit: 'gal',
    alternateUnits: ['L', 'cups'],
    conversions: {
      'gal': 1,
      'L': 3.78541,      // 1 gallon = 3.78541 liters
      'cups': 16,        // 1 gallon = 16 cups (US)
    }
  },
  distance: {
    baseUnit: 'mi',
    alternateUnits: ['km'],
    conversions: {
      'mi': 1,
      'km': 1.60934,     // 1 mile = 1.60934 km
    }
  },
  time: {
    baseUnit: 'hrs',
    alternateUnits: ['min'],
    conversions: {
      'hrs': 1,
      'min': 60,         // 1 hour = 60 minutes
    }
  },
  weight: {
    baseUnit: 'lbs',
    alternateUnits: ['kg'],
    conversions: {
      'lbs': 1,
      'kg': 0.453592,    // 1 pound = 0.453592 kg
    }
  }
};

// Map metric keys to their unit groups
export const METRIC_UNIT_GROUPS: Record<string, string> = {
  'water_gallons': 'volume',
  'miles_ran': 'distance',
  'sleep': 'time',
  'gym_time': 'time', // Note: gym_time base unit is actually 'min', needs special handling
  'body_weight': 'weight',
  'healthkit_exercise_time': 'time',
  'healthkit_sleep_time': 'time',
  'healthkit_stand_hours': 'time',
};

// Get the base unit for a metric
export const getMetricBaseUnit = (metricKey: string): UnitType => {
  // Special case: gym_time's base unit is 'min' not 'hrs'
  if (metricKey === 'gym_time' || metricKey === 'healthkit_exercise_time') {
    return 'min';
  }
  if (metricKey === 'healthkit_stand_hours') {
    return 'hrs';
  }
  
  const groupName = METRIC_UNIT_GROUPS[metricKey];
  if (!groupName) return 'g'; // Default fallback
  
  const group = UNIT_GROUPS[groupName];
  return group ? group.baseUnit : 'g';
};

// Get available units for a metric
export const getAvailableUnits = (metricKey: string): UnitType[] => {
  // Special handling for gym_time - it's stored in minutes but can display as hours
  if (metricKey === 'gym_time' || metricKey === 'healthkit_exercise_time') {
    return ['min', 'hrs'];
  }
  
  // Special handling for stand hours
  if (metricKey === 'healthkit_stand_hours') {
    return ['hrs', 'min'];
  }
  
  const groupName = METRIC_UNIT_GROUPS[metricKey];
  if (!groupName) return [];
  
  const group = UNIT_GROUPS[groupName];
  if (!group) return [];
  
  return [group.baseUnit, ...group.alternateUnits];
};

// Convert a value from base unit to target unit
export const convertFromBase = (
  value: number,
  metricKey: string,
  targetUnit: UnitType
): number => {
  const baseUnit = getMetricBaseUnit(metricKey);
  
  // If target is same as base, no conversion needed
  if (baseUnit === targetUnit) return value;
  
  // Special handling for gym_time conversions (base is min)
  if (metricKey === 'gym_time' || metricKey === 'healthkit_exercise_time') {
    if (baseUnit === 'min' && targetUnit === 'hrs') {
      return value / 60;
    }
    if (baseUnit === 'hrs' && targetUnit === 'min') {
      return value * 60;
    }
  }
  
  // Special handling for stand hours
  if (metricKey === 'healthkit_stand_hours') {
    if (baseUnit === 'hrs' && targetUnit === 'min') {
      return value * 60;
    }
    if (baseUnit === 'min' && targetUnit === 'hrs') {
      return value / 60;
    }
  }
  
  // Handle sleep time conversions
  if (metricKey === 'sleep' || metricKey === 'healthkit_sleep_time') {
    if (baseUnit === 'hrs' && targetUnit === 'min') {
      return value * 60;
    }
    if (baseUnit === 'min' && targetUnit === 'hrs') {
      return value / 60;
    }
  }
  
  const groupName = METRIC_UNIT_GROUPS[metricKey];
  if (!groupName) return value;
  
  const group = UNIT_GROUPS[groupName];
  if (!group) return value;
  
  const targetMultiplier = group.conversions[targetUnit];
  if (!targetMultiplier) return value;
  
  return value * targetMultiplier;
};

// Convert a value from any unit back to base unit
export const convertToBase = (
  value: number,
  metricKey: string,
  fromUnit: UnitType
): number => {
  const baseUnit = getMetricBaseUnit(metricKey);
  
  // If from is same as base, no conversion needed
  if (baseUnit === fromUnit) return value;
  
  // Special handling for gym_time conversions (base is min)
  if (metricKey === 'gym_time' || metricKey === 'healthkit_exercise_time') {
    if (baseUnit === 'min' && fromUnit === 'hrs') {
      return value * 60;
    }
    if (baseUnit === 'hrs' && fromUnit === 'min') {
      return value / 60;
    }
  }
  
  // Special handling for stand hours
  if (metricKey === 'healthkit_stand_hours') {
    if (baseUnit === 'hrs' && fromUnit === 'min') {
      return value / 60;
    }
    if (baseUnit === 'min' && fromUnit === 'hrs') {
      return value * 60;
    }
  }
  
  // Handle sleep time conversions
  if (metricKey === 'sleep' || metricKey === 'healthkit_sleep_time') {
    if (baseUnit === 'hrs' && fromUnit === 'min') {
      return value / 60;
    }
    if (baseUnit === 'min' && fromUnit === 'hrs') {
      return value * 60;
    }
  }
  
  const groupName = METRIC_UNIT_GROUPS[metricKey];
  if (!groupName) return value;
  
  const group = UNIT_GROUPS[groupName];
  if (!group) return value;
  
  const fromMultiplier = group.conversions[fromUnit];
  if (!fromMultiplier) return value;
  
  return value / fromMultiplier;
};

// Format value with appropriate decimal places
export const formatValueWithUnit = (
  value: number,
  unit: UnitType,
  metricKey: string
): string => {
  // For minutes, use whole numbers
  if (unit === 'min' || unit === 'steps' || unit === 'commits') {
    return `${Math.round(value)} ${unit}`;
  }
  
  // For cups, use whole numbers or 0.5
  if (unit === 'cups') {
    return `${Math.round(value * 2) / 2} ${unit}`;
  }
  
  // For most other units, use 1 decimal place
  if (unit === 'hrs' || unit === 'mi' || unit === 'km' || unit === 'lbs' || unit === 'kg') {
    return `${value.toFixed(1)} ${unit}`;
  }
  
  // For liters and gallons, use 2 decimal places
  if (unit === 'L' || unit === 'gal') {
    return `${value.toFixed(2)} ${unit}`;
  }
  
  // Default
  return `${value} ${unit}`;
};
